// ─── Auth & Data Persistence via localStorage ──────────────────
//
// Key schema:
//   alberto:users              → Record<email, StoredUser>
//   alberto:session            → Session | null
//   alberto:data:<email>:<key> → per-user mutable dashboard state

export type AuthMode = 'mock' | 'real'

export interface StoredUser {
  email: string
  name: string
  passwordHash: string
  createdAt: string
}

export interface Session {
  email: string
  name: string
  mode: AuthMode
  loggedInAt: string
}

export type AuthResult =
  | { ok: true; user: StoredUser }
  | { ok: false; error: string }

const USERS_KEY = 'alberto:users'
const SESSION_KEY = 'alberto:session'
const THEME_PREFERENCE_KEY = 'alberto:themePreference'
const USER_DATA_PREFIX = 'alberto:data'
const APP_STORAGE_PREFIX = 'alberto:'
const RESET_PRESERVED_KEYS = new Set([USERS_KEY, SESSION_KEY, THEME_PREFERENCE_KEY])
const PRESERVED_USER_STATE_SUFFIXES = new Set([
  'openRouterSettings',
  'mockOpenRouterSettings',
  'personalProfile',
  'mockPersonalProfile',
])

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

function normalizeName(name: string): string {
  return name.trim().replace(/\s+/g, ' ')
}

function parseStored<T>(raw: string | null, fallback: T, storageKey: string): T {
  if (!raw) return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    throw new Error(`[LOCAL_STORAGE_PARSE_ERROR] Failed to parse localStorage key "${storageKey}".`)
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      throw new Error(`[LOCAL_STORAGE_QUOTA_EXCEEDED] localStorage quota exceeded while writing "${key}".`)
    }

    throw new Error(`[LOCAL_STORAGE_WRITE_FAILED] Failed to write localStorage key "${key}".`)
  }
}

// ─── Password hashing (SHA-256 via Web Crypto) ──────────────────

async function hashPassword(password: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle
  if (!subtle) {
    throw new Error('[CRYPTO_SUBTLE_UNAVAILABLE] Secure Web Crypto hashing is unavailable.')
  }

  const data = new TextEncoder().encode(password)

  try {
    const hashBuffer = await subtle.digest('SHA-256', data)
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    throw new Error('[CRYPTO_HASH_FAILED] Secure password hashing failed.')
  }
}

// ─── User registry ──────────────────────────────────────────────

function getUsers(): Record<string, StoredUser> {
  return parseStored<Record<string, StoredUser>>(localStorage.getItem(USERS_KEY), {}, USERS_KEY)
}

function saveUsers(users: Record<string, StoredUser>): void {
  safeSetItem(USERS_KEY, JSON.stringify(users))
}

export async function registerUser(
  email: string,
  name: string,
  password: string
): Promise<AuthResult> {
  const normalizedEmail = normalizeEmail(email)
  const normalizedName = normalizeName(name)
  if (!normalizedEmail) return { ok: false, error: 'Email is required.' }
  if (!normalizedName) return { ok: false, error: 'Name is required.' }

  const users = getUsers()
  if (users[normalizedEmail]) {
    return { ok: false, error: 'An account with this email already exists.' }
  }

  const passwordHash = await hashPassword(password)
  const user: StoredUser = {
    email: normalizedEmail,
    name: normalizedName,
    passwordHash,
    createdAt: new Date().toISOString(),
  }
  users[normalizedEmail] = user
  saveUsers(users)
  return { ok: true, user }
}

export async function authenticateUser(
  email: string,
  password: string
): Promise<AuthResult> {
  const normalizedEmail = normalizeEmail(email)
  if (!normalizedEmail) return { ok: false, error: 'Email is required.' }

  const users = getUsers()
  const user = users[normalizedEmail]
  if (!user) {
    return { ok: false, error: 'No account found with this email.' }
  }
  const hash = await hashPassword(password)
  if (hash !== user.passwordHash) {
    return { ok: false, error: 'Incorrect password.' }
  }
  return { ok: true, user }
}

// ─── Session management ─────────────────────────────────────────

export function saveSession(session: Session): void {
  const normalizedSession: Session = {
    email: normalizeEmail(session.email),
    loggedInAt: session.loggedInAt || new Date().toISOString(),
    mode: session.mode,
    name: normalizeName(session.name),
  }
  safeSetItem(SESSION_KEY, JSON.stringify(normalizedSession))
}

export function loadSession(): Session | null {
  const raw = parseStored<Session | null>(localStorage.getItem(SESSION_KEY), null, SESSION_KEY)
  if (!raw) return null

  const email = normalizeEmail(raw.email ?? '')
  const name = normalizeName(raw.name ?? '')
  if (!email || !name) return null
  if (raw.mode !== 'real' && raw.mode !== 'mock') return null

  return {
    email,
    loggedInAt: raw.loggedInAt || new Date().toISOString(),
    mode: raw.mode,
    name,
  }
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY)
}

function shouldPreserveOnAppReset(key: string): boolean {
  if (RESET_PRESERVED_KEYS.has(key)) {
    return true
  }

  if (!key.startsWith(`${USER_DATA_PREFIX}:`)) {
    return false
  }

  const lastSeparator = key.lastIndexOf(':')
  if (lastSeparator < 0) {
    return false
  }

  const stateKey = key.slice(lastSeparator + 1)
  return PRESERVED_USER_STATE_SUFFIXES.has(stateKey)
}

export function clearAppLocalStorage(): void {
  const keysToRemove: string[] = []

  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index)
    if (!key) {
      continue
    }

    if (key.startsWith(APP_STORAGE_PREFIX) && !shouldPreserveOnAppReset(key)) {
      keysToRemove.push(key)
    }
  }

  keysToRemove.forEach((key) => {
    localStorage.removeItem(key)
  })
}

// ─── Per-user data persistence ──────────────────────────────────

function userDataKey(email: string, stateKey: string): string {
  return `${USER_DATA_PREFIX}:${normalizeEmail(email)}:${stateKey}`
}

export function loadUserData<T>(email: string, stateKey: string): T | null {
  const storageKey = userDataKey(email, stateKey)
  return parseStored<T | null>(localStorage.getItem(storageKey), null, storageKey)
}

export function saveUserData<T>(email: string, stateKey: string, value: T): void {
  const storageKey = userDataKey(email, stateKey)
  safeSetItem(storageKey, JSON.stringify(value))
}
